/**
 * @file BlendSummary.js
 * @version 1.8.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-13
 * @modified 2026-09-10
 * @description The live panel beside the grid: does this blend total 100, does
 *              it meet its spec, and what does it cost delivered.
 *
 *              The workbook buried the per-material cost contribution in two
 *              hidden columns; it is the fastest way to see what is actually
 *              driving a price, so it gets a bar chart here rather than a
 *              spreadsheet column nobody scrolled to.
 *
 *              Nutrient tiles show a target band when a spec is attached. An
 *              unmeasured nutrient reads "unknown", never "pass" — a blend that
 *              simply has no figure for protein has not met a protein target.
 */

import React, { useMemo } from 'react';
import { formatCurrency } from '../utils/currencyFormatter';
import useScrollAffordance from '../hooks/useScrollAffordance';
import ScrollMore from './ScrollMore';

const fmt = (v, d = 2) =>
  (v === null || v === undefined || !Number.isFinite(Number(v))) ? '—' : Number(v).toFixed(d);

/**
 * A spec gauge.
 *
 * The old tile drew the band and a dot, which answered "am I inside" and
 * nothing else. Standing on a minimum and clearing it by three points look
 * identical there, and they are not the same blend at all — one of them has
 * nowhere to go when a material moves and the other absorbs the change without
 * anyone noticing. So the gauge shows the distance as well as the verdict:
 * where the limits are, where the value sits between them, and how much room is
 * left on the side that is nearest.
 *
 * A one-sided target is still a gauge, not half a broken one — the open side
 * gets an arrow rather than a tick, because "no upper limit" is a fact worth
 * drawing rather than an absence.
 */
function SpecGauge({ component, value, target, compareValue = null, comparing = false }) {
  const status = target?.status || null;
  const has = value !== undefined && value !== null;
  const decimals = component.decimals ?? 2;

  const gauge = useMemo(() => {
    if (!target || !has) return null;
    const min = target.min ?? null;
    const max = target.max ?? null;
    if (min === null && max === null) return null;

    // The drawn range has to hold the band AND wherever the value actually is,
    // or an out-of-spec blend pins to an edge and stops telling you how far out
    // it is — which is the one moment the gauge earns its place.
    const lo = min !== null ? min : max;
    const hi = max !== null ? max : min;
    const span = (hi - lo) || Math.abs(hi) * 0.2 || 1;
    let displayLo = lo - span * 0.45;
    let displayHi = hi + span * 0.45;
    if (value < displayLo) displayLo = value - span * 0.15;
    if (value > displayHi) displayHi = value + span * 0.15;
    const width = displayHi - displayLo || 1;
    const at = (v) => ((v - displayLo) / width) * 100;

    // Headroom against the limit it is nearest to. That is the number that
    // says whether this blend has any give in it.
    let room = null;
    if (min !== null && max !== null) {
      const toMin = value - min;
      const toMax = max - value;
      room = toMin <= toMax
        ? { amount: toMin, edge: 'min' }
        : { amount: toMax, edge: 'max' };
    } else if (min !== null) {
      room = { amount: value - min, edge: 'min' };
    } else {
      room = { amount: max - value, edge: 'max' };
    }

    return {
      value: Math.max(0, Math.min(100, at(value))),
      compareAt: compareValue == null ? null : Math.max(0, Math.min(100, at(compareValue))),
      bandStart: min !== null ? Math.max(0, at(min)) : 0,
      bandEnd: max !== null ? Math.min(100, at(max)) : 100,
      minAt: min !== null ? at(min) : null,
      maxAt: max !== null ? at(max) : null,
      min, max, room
    };
  }, [target, value, has, compareValue]);

  return (
    <div className={`blend-tile blend-gauge${status ? ` status-${status}` : ''}`}>
      <span className="blend-tile-label">{component.label}</span>
      <span className="blend-tile-value">
        {comparing && (
          <span className="blend-ab-pair">
            <span className="blend-ab a">
              <span className="blend-ab-tag is-a">A</span>
              {has ? fmt(value, decimals) : '—'}
            </span>
            <span className="blend-ab b">
              <span className="blend-ab-tag is-b">B</span>
              {compareValue == null ? '—' : fmt(compareValue, decimals)}
            </span>
          </span>
        )}
        {!comparing && (has ? fmt(value, decimals) : '—')}
        <span className="blend-tile-unit">{component.unit}</span>
      </span>

      {target && (
        <>
          <div className="blend-tile-track">
            {gauge && (
              <>
                <span
                  className="blend-tile-band"
                  style={{ left: `${gauge.bandStart}%`, width: `${gauge.bandEnd - gauge.bandStart}%` }}
                />
                {/* An open side is drawn open. A tick there would claim a limit
                    that the spec does not set. */}
                {gauge.minAt === null && <span className="bg-open bg-open-left" />}
                {gauge.maxAt === null && <span className="bg-open bg-open-right" />}
                {gauge.minAt !== null && (
                  <span className="bg-tick" style={{ left: `${gauge.minAt}%` }} />
                )}
                {gauge.maxAt !== null && (
                  <span className="bg-tick" style={{ left: `${gauge.maxAt}%` }} />
                )}
                {/* B's mark sits on the same track as A's, so the move is a
                    distance on one scale rather than two numbers to subtract.
                    Hollow, because the filled one is the blend being edited. */}
                {gauge.compareAt !== null && (
                  <span className="blend-tile-marker is-ref" style={{ left: `${gauge.compareAt}%` }} />
                )}
                <span className="blend-tile-marker" style={{ left: `${gauge.value}%` }} />
              </>
            )}
          </div>

          <span className="blend-tile-target">
            {target.status === 'unknown' ? (
              'not measured'
            ) : gauge ? (
              <>
                <span className="bg-limits">
                  {gauge.min ?? '–'} – {gauge.max ?? '–'}
                </span>
                {/* Sitting on a limit is worth saying out loud: it means the
                    next change to any material moves this out of spec. */}
                <span className={`bg-room${gauge.room.amount < 0 ? ' out' : gauge.room.amount < 0.05 ? ' tight' : ''}`}>
                  {gauge.room.amount < 0
                    ? `${fmt(Math.abs(gauge.room.amount), decimals)} over`
                    : gauge.room.amount < 0.05
                      ? `on the ${gauge.room.edge === 'min' ? 'floor' : 'ceiling'}`
                      : `${fmt(gauge.room.amount, decimals)} spare`}
                </span>
              </>
            ) : (
              `${target.min ?? '–'} – ${target.max ?? '–'}`
            )}
          </span>
        </>
      )}
    </div>
  );
}

export default function BlendSummary({
  summary,
  columns = [],
  haulageBands = [],
  nutrientComponents = [],
  priceColumnKey,
  onSelectPriceColumn,
  spec = null,
  asOf,
  freshness = [],
  onBalance,
  // When comparing, the frozen side's figures. Every number the panel shows
  // gains a delta against it rather than the panel becoming a second table —
  // the question is "what did my change do", and that reads best beside the
  // figure it changed.
  compareSummary = null,
  compareName = null,
  // Set when A was rebuilt from a non-MVFS recipe using our materials. The
  // caveat belongs here rather than beside the comparison: every figure below
  // it is computed from OUR stand-ins, so it is a note on the analysis.
  rebuiltFrom = null
}) {
  const comparing = !!compareSummary;
  const compareActive = compareSummary?.pricing?.[priceColumnKey];

  /**
   * A signed difference against the reference, with the class that colours it.
   * Cheaper is good on a cost, so down is green here — the opposite of the
   * convention on a nutrient, which is why the caller says which it wants.
   */
  const delta = (now, before, { decimals = 2, goodWhenDown = false, money = false } = {}) => {
    if (!comparing || now == null || before == null) return null;
    const d = Number(now) - Number(before);
    // Below what the reader can see, there is nothing to report.
    if (Math.abs(d) < 10 ** -decimals / 2) {
      return <span className="blend-delta same">—</span>;
    }
    const good = goodWhenDown ? d < 0 : d > 0;
    const text = money
      ? `${d > 0 ? '+' : '−'}${formatCurrency(Math.abs(d))}`
      : `${d > 0 ? '+' : '−'}${Math.abs(d).toFixed(decimals)}`;
    return <span className={`blend-delta ${good ? 'good' : 'bad'}`}>{text}</span>;
  };

  const balanced = summary?.isBalanced;
  const remaining = summary?.remaining ?? 0;
  const active = summary?.pricing?.[priceColumnKey];

  // Oldest price in the selected column — the staleness pill.
  const stale = useMemo(() => {
    const col = columns.find((c) => c.key === priceColumnKey);
    if (!col) return null;
    const row = freshness.find((f) => f.price_column_id === col.id);
    if (!row?.latest_effective_date) return null;
    const date = new Date(row.latest_effective_date);
    const days = Math.floor((new Date(asOf) - date) / 86400000);
    return { date, days };
  }, [columns, freshness, priceColumnKey, asOf]);

  const topContributions = useMemo(() => {
    const list = (active?.contributions || []).filter((c) => c.contribution != null);
    return list.slice(0, 8);
  }, [active]);

  /**
   * The reference's contribution for the same materials.
   *
   * "What's driving the cost" is the panel you look at to answer "why did that
   * get dearer", and without A beside B it could only tell you the after — you
   * were left subtracting from memory. Keyed by material rather than by rank,
   * because the order changes between the two blends and comparing the third
   * bar to the third bar would compare different materials.
   */
  const compareContributions = useMemo(() => {
    const map = new Map();
    for (const c of compareActive?.contributions || []) map.set(c.key, c.contribution);
    return map;
  }, [compareActive]);

  const maxContribution = Math.max(
    topContributions[0]?.contribution || 1,
    ...(comparing ? topContributions.map((c) => compareContributions.get(c.key) || 0) : [0])
  );

  // Figures at the foot of a panel that ends level with its frame go unread.
  const [scrollRef, scroll] = useScrollAffordance([summary, spec, nutrientComponents, comparing]);

  return (
    <div className="scroll-more-wrap">
    <aside ref={scrollRef} className="blend-summary scroll-more-box">
      {rebuiltFrom && (
        <div className="blend-rebuilt-note">
          <span className="material-symbols-rounded">swap_horiz</span>
          <span>
            Rebuilt from <strong>{rebuiltFrom.item_code || rebuiltFrom.name}</strong> using
            our materials. Everything below is ours, not theirs — check it against B.
          </span>
        </div>
      )}

      {/* Running total. This is the single most important number on the screen:
          the arithmetic divides by 100 rather than by the actual total, exactly
          as the workbook does, so a blend at 98% understates every figure by 2%
          with nothing else to warn you. */}
      <button
        type="button"
        className={`blend-total-pill${balanced ? ' balanced' : ''}`}
        onClick={onBalance}
        disabled={balanced}
        title={balanced ? 'Balanced' : 'Distribute the difference across the unlocked materials'}
      >
        <span className="blend-total-figure">{fmt(summary?.total)}%</span>
        <span className="blend-total-note">
          {balanced
            ? 'balanced'
            : `${remaining > 0 ? '+' : ''}${fmt(remaining)}% to go — click to balance`}
        </span>
      </button>

      {summary?.unknownMaterials?.length > 0 && (
        <div className="blend-warning">
          <span className="material-symbols-rounded">error</span>
          <span>
            {summary.unknownMaterials.length} material(s) in this blend are no longer
            in the master list. Their inclusions are counted but they cannot be priced.
          </span>
        </div>
      )}

      <section className="blend-panel">
        <h3 className="blend-panel-title">Analysis</h3>
        <div className="blend-tiles">
          {nutrientComponents.map((c) => (
            <SpecGauge
              key={c.key}
              component={c}
              value={summary?.nutrients?.[c.key]}
              target={summary?.spec?.results?.[c.key] || null}
              comparing={comparing}
              compareValue={compareSummary?.nutrients?.[c.key] ?? null}
            />
          ))}
        </div>
        {summary?.partialNutrients?.length > 0 && (
          <p className="blend-panel-note">
            <span className="material-symbols-rounded">info</span>
            Some materials have no figure for{' '}
            {summary.partialNutrients.map((p) => {
              const c = nutrientComponents.find((n) => n.key === p.key);
              return c ? c.label : p.key;
            }).join(', ')}
            {' '}— those results are understated.
          </p>
        )}
        {spec && (
          <p className={`blend-spec-verdict${summary?.spec?.pass ? ' pass' : ' fail'}`}>
            <span className="material-symbols-rounded">
              {summary?.spec?.pass ? 'check_circle' : 'cancel'}
            </span>
            {summary?.spec?.pass ? `Meets ${spec.name}` : `Does not meet ${spec.name}`}
          </p>
        )}
      </section>

      <section className="blend-panel">
        <h3 className="blend-panel-title">
          Cost
          {stale && (
            <span
              className={`blend-stale-pill${stale.days > 45 ? ' stale' : ''}`}
              title={`Prices last updated ${stale.date.toLocaleDateString('en-GB')}`}
            >
              {stale.days === 0 ? 'updated today' : `${stale.days} days old`}
            </span>
          )}
        </h3>

        <div className="blend-col-switch">
          {columns.map((c) => (
            <button
              key={c.key}
              type="button"
              className={`blend-col-btn${c.key === priceColumnKey ? ' active' : ''}`}
              onClick={() => onSelectPriceColumn(c.key)}
            >
              <span className="blend-col-label">{c.label}</span>
              <span className="blend-col-figure">
                {summary?.pricing?.[c.key]
                  ? formatCurrency(summary.pricing[c.key].rawMaterialCost)
                  : '—'}
              </span>
            </button>
          ))}
        </div>

        {active?.missing?.length > 0 && (
          <div className="blend-warning">
            <span className="material-symbols-rounded">warning</span>
            {/* Exports are NOT blocked any more — they carry the unpriced
                materials, marked, with a notice saying the price excludes
                them. This still said "blocked", which is the sentence that
                would talk somebody out of trying. */}
            <span>
              No price for {active.missing.length} material(s) in this column, so the
              cost below is understated. A quote or comparison can still be exported —
              the unpriced materials are marked on it, and it says the price excludes them.
            </span>
          </div>
        )}

        <table className={`data-table blend-price-table${comparing ? ' comparing' : ''}`}>
          {comparing && (
            <thead>
              <tr>
                <th />
                <th className="blend-ab-head"><span className="blend-ab-tag is-a">A</span></th>
                <th className="blend-ab-head"><span className="blend-ab-tag is-b">B</span></th>
              </tr>
            </thead>
          )}
          <tbody>
            <tr className="blend-price-rm">
              <td>Raw material</td>
              <td className="num">
                {active ? formatCurrency(active.rawMaterialCost) : '—'}
                {delta(active?.rawMaterialCost, compareActive?.rawMaterialCost,
                       { goodWhenDown: true, money: true })}
              </td>
              {comparing && (
                <td className="num blend-ref-cell">
                  {compareActive ? formatCurrency(compareActive.rawMaterialCost) : '—'}
                </td>
              )}
            </tr>
            {haulageBands.map((b) => (
              <tr key={b.key}>
                <td>
                  {b.label}
                  <span className="blend-band-mode">{b.packaging === 'tote' ? 'tote' : 'bulk'}</span>
                </td>
                <td className="num">
                  {active ? formatCurrency(active.delivered[b.key]) : '—'}
                  {delta(active?.delivered?.[b.key], compareActive?.delivered?.[b.key],
                         { goodWhenDown: true, money: true })}
                </td>
                {comparing && (
                  <td className="num blend-ref-cell">
                    {compareActive ? formatCurrency(compareActive.delivered[b.key]) : '—'}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {topContributions.length > 0 && (
        <section className="blend-panel">
          <h3 className="blend-panel-title">What's driving the cost</h3>
          <ul className="blend-contrib">
            {topContributions.map((c) => (
              <li key={c.key}>
                <span className="blend-contrib-name" title={c.name}>{c.name}</span>
                <span className="blend-contrib-bar">
                  {/* A's bar sits behind B's on the same scale, so a material
                      that grew and one that shrank are told apart by which
                      overhangs which. */}
                  {comparing && compareContributions.has(c.key) && (
                    <span className="blend-contrib-ref"
                          style={{ width: `${(compareContributions.get(c.key) / maxContribution) * 100}%` }} />
                  )}
                  <span style={{ width: `${(c.contribution / maxContribution) * 100}%` }} />
                </span>
                <span className="blend-contrib-figure">
                  {comparing ? (
                    <span className="blend-ab-pair">
                      <span className="blend-ab a">
                        <span className="blend-ab-tag is-a">A</span>
                        {formatCurrency(c.contribution)}
                      </span>
                      <span className="blend-ab b">
                        <span className="blend-ab-tag is-b">B</span>
                        {/* £0.00, not a dash. "B does not use this material" is
                            a cost of nothing, which is a figure; a dash reads as
                            "not applicable" and invites a second look. */}
                        {formatCurrency(compareContributions.get(c.key) || 0)}
                      </span>
                    </span>
                  ) : (
                    <>
                      {formatCurrency(c.contribution)}
                      <span className="blend-contrib-share">{fmt(c.shareOfCost, 0)}%</span>
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </aside>
      <ScrollMore scrollRef={scrollRef} remaining={scroll.remaining} />
    </div>
  );
}
